/**
 * Direct activity ingest — our replacement for Amplitude's event-streaming destination.
 *
 * WHY THIS EXISTS
 *     Investor browsing behaviour (time on page, images viewed, documents opened) reaches
 *     Clarity by exactly one route: the browser sends an event to Amplitude, and Amplitude's
 *     streaming destination POSTs it back to `POST /amplitude/webhook`, which writes an
 *     `investor_activities` row and relays the payload on to Salesforce. That single hop
 *     through a third party is the only supply of web-behaviour signal to the interest score.
 *
 *     We lost access to the Amplitude account, so that hop can no longer be relied on or
 *     reconfigured. This module posts the same envelope straight to our own API, removing
 *     Amplitude from the critical path.
 *
 * RUNS ALONGSIDE AMPLITUDE, NOT INSTEAD OF IT
 *     We cannot turn the Amplitude destination off — turning it off requires the account we
 *     lost. It may still be firing. So the server tags rows from this path `src='web'` rather
 *     than `src='amplitude'`: if both paths are live the rows stay distinguishable and nothing
 *     silently double-counts. Deduplication is by source, not by guessing Amplitude's
 *     `$insert_id` (which is generated inside Amplitude and cannot be reproduced here).
 *
 * THIS IS NOT AN AUTHENTICATED CHANNEL
 *     Several of the events that matter most fire before the investor has a token at all —
 *     the share-link click, OTP start, OTP verify. We attach the investor bearer token when
 *     one exists, but the endpoint has to accept anonymous posts or we lose exactly the
 *     top-of-funnel events the score depends on. The server compensates by requiring `sfid`
 *     to resolve to a real contact and by rate limiting. A signed share link is the real fix
 *     and is deliberately out of scope here.
 *
 * FAILURE POSTURE
 *     Tracking must never break the page it is measuring. Every entry point swallows its own
 *     errors, every storage access is guarded (Safari private mode throws on write), and a
 *     failed post is buffered and retried on next load rather than lost — a dropped event is
 *     a hole in the score, which is the thing we are trying to repair.
 */

const INGEST_PATH = "/amplitude/ingest";

/**
 * OFF UNLESS EXPLICITLY ENABLED — set REACT_APP_ACTIVITY_INGEST=true at build time.
 *
 * This module was written when we believed the Amplitude account loss had cut the interest
 * score's input. It had not: the relay is alive and the score is fresh, so this path is
 * insurance against a FUTURE loss, not a fix for a current one.
 *
 * It ships disabled because the server route it posts to (`POST /amplitude/ingest`) is not
 * deployed. Without this flag every tracked event would fire a request that 404s — harmless,
 * since a 4xx is treated as handled and never buffered, but it is dead traffic in production
 * and exactly the kind of thing that wastes an hour of someone's debugging six months from now.
 *
 * To turn on: deploy the backend ingest route, then rebuild with the flag set.
 */
const INGEST_ENABLED = process.env.REACT_APP_ACTIVITY_INGEST === "true";

/** Events awaiting retry, persisted so a failed send survives the tab closing. */
const BUFFER_KEY = "activity-ingest-buffer";

/**
 * Hard cap on buffered events. A user who is offline for a long session would otherwise
 * grow this without bound and eventually blow the localStorage quota, which throws on the
 * *next* write and would take the buffer out entirely. Oldest events are dropped first.
 */
const BUFFER_LIMIT = 200;

const SESSION_KEY = "activity-ingest-session";

/** Amplitude's own session timeout, mirrored so sessions stay comparable across the cutover. */
const SESSION_IDLE_MS = 30 * 60 * 1000;

const SESSION_TOUCHED_KEY = "activity-ingest-session-touched";

type Envelope = {
  $insert_id: string;
  event_type: string;
  event_time: string;
  session_id: number | null;
  user_id: string | null;
  event_properties: Record<string, any>;
};

/** Storage access throws outright in some privacy modes — never let that reach the caller. */
const safeGet = (store: Storage, key: string): string | null => {
  try {
    return store.getItem(key);
  } catch {
    return null;
  }
};

const safeSet = (store: Storage, key: string, value: string): void => {
  try {
    store.setItem(key, value);
  } catch {
    /* quota or blocked storage — the event still goes out on the live request */
  }
};

const newId = (): string => {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
  } catch {
    /* fall through */
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
};

/**
 * Amplitude models a session as the epoch-ms of its first event, expiring after 30 idle
 * minutes. Reproducing that here keeps `src_session_id` meaningful when rows from the old
 * Amplitude path and the new direct path sit side by side in the same table.
 */
const getSessionId = (): number | null => {
  try {
    const now = Date.now();
    const touched = Number(safeGet(window.localStorage, SESSION_TOUCHED_KEY) || 0);
    let session = Number(safeGet(window.localStorage, SESSION_KEY) || 0);

    if (!session || !touched || now - touched > SESSION_IDLE_MS) {
      session = now;
      safeSet(window.localStorage, SESSION_KEY, String(session));
    }
    safeSet(window.localStorage, SESSION_TOUCHED_KEY, String(now));
    return session;
  } catch {
    return null;
  }
};

const readBuffer = (): Envelope[] => {
  const raw = safeGet(window.localStorage, BUFFER_KEY);
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writeBuffer = (events: Envelope[]): void => {
  try {
    if (events.length === 0) {
      window.localStorage.removeItem(BUFFER_KEY);
      return;
    }
    safeSet(window.localStorage, BUFFER_KEY, JSON.stringify(events.slice(-BUFFER_LIMIT)));
  } catch {
    /* nothing useful to do — dropping the buffer beats breaking the page */
  }
};

const buffer = (envelope: Envelope): void => {
  writeBuffer([...readBuffer(), envelope]);
};

/**
 * `keepalive` is not optional here. The single most valuable event we collect — Total Time
 * Spent — is emitted from `beforeunload`/`visibilitychange` in PageTracker, and a normal
 * fetch is cancelled the moment the document starts tearing down. Without this flag the
 * dwell-time signal is lost on every real page exit and kept only when the user navigates
 * within the SPA, which would bias the score toward short visits.
 */
const post = async (payload: Envelope | Envelope[]): Promise<boolean> => {
  const base = process.env.REACT_APP_BASE_URL;
  if (!base) return false;

  const headers: Record<string, string> = { "Content-Type": "application/json" };
  const token = safeGet(window.localStorage, "investor-access-token");
  if (token) headers.Authorization = `Bearer ${token}`;

  try {
    const response = await fetch(`${base.replace(/\/$/, "")}${INGEST_PATH}`, {
      method: "POST",
      headers,
      body: JSON.stringify(payload),
      keepalive: true,
      credentials: "omit"
    });
    // 4xx means the server rejected the shape or the contact is unknown. Retrying will not
    // change that answer, so treat it as handled and let the row go — only retry transport
    // and server faults.
    return response.ok || (response.status >= 400 && response.status < 500);
  } catch {
    return false;
  }
};

/**
 * Flush anything left over from a previous load. Called once at module init; failures are
 * put straight back so a long outage does not quietly erase the backlog.
 */
const flushBuffer = (): void => {
  const pending = readBuffer();
  if (pending.length === 0) return;
  writeBuffer([]);
  post(pending).then((ok) => {
    if (!ok) writeBuffer(pending);
  });
};

/**
 * Send one tracked event to our own ingest.
 *
 * `properties` is passed through verbatim: the investor-side event properties already use
 * the exact field names the server reads (`sfid`, `pubPropertySFID`, `propertySFID`,
 * `opportunitySFID`, `linkCId`, `linkEmailId`, `shareId`, `mobileNumber`, `DocumentName`,
 * `DocumentUrl`, `timeSpent`, `totalImagesViewed`). Renaming anything here would silently
 * detach the field from the score.
 */
export const ingestActivity = (
  eventName: string,
  properties: Record<string, any>
): void => {
  try {
    if (!INGEST_ENABLED) return;

    // The server resolves the contact by `sfid` and drops the row when it cannot. Posting
    // without one buys nothing and still costs a Salesforce relay, so stop here instead.
    if (!properties || !properties.sfid) return;

    const envelope: Envelope = {
      $insert_id: newId(),
      event_type: eventName,
      event_time: new Date().toISOString(),
      session_id: getSessionId(),
      user_id: properties.userId || properties.sfid || null,
      event_properties: properties
    };

    post(envelope).then((ok) => {
      if (!ok) buffer(envelope);
    });
  } catch {
    /* tracking must never take the page down with it */
  }
};

// Flush anything a previous session left behind — but only when the path is enabled, so a
// disabled build never talks to the server at all.
try {
  if (INGEST_ENABLED) flushBuffer();
} catch {
  /* ignore */
}
